import React, { useContext } from 'react';
import { Box, Grid, IconButton, List, ListItem } from '@mui/material';
import { Typography } from '@uhc-tempo/components';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import { FormContext } from '../../../context/Form.context';
import { Warning, Square, Error } from '@mui/icons-material';
import {
    SHELL_INN_PROVIDER,
    CARE_PATH_CATEGORY_CODE,
    ENTER_KEY,
    CURSOR_POINTER,
    LOGIN_ROUTE,
    VISION_MATERIALS,
    NO_BENEFITS,
    LOW_USAGE_CODE,
    REV_DUPLICATE,
    UMR_POPULATIONS,
    LINE_OF_BUSINESS,
    UMR_ENI_VISION_NOBENEFITS,
} from '../../../constants';
import {
    shouldShowDentalPolicyDisclaimer,
    checkForRevcodeServiceType,
    checkBehavioralMedicalDisclaimerCondition,
    checkForRevNoEstimateServiceType,
    checkIfMemberHasVisionCoverageType,
} from '../../../utils/SearchPageUtil';
import { useHistory } from 'react-router-dom';

/**
 * Component that displays the blue disclaimer/warning message on the search page
 * when a member selects a service and either does not have coverage for that service
 * or they do have coverage but do not have valid PNI/networks and we can't search for providers for them
 */
const ServiceDisclaimerBanner = (props) => {
    const {
        isIndemnity,
        noCoverageDisclaimerOpen,
        setProvider,
        handleSubmit,
        setNoCoverageDisclaimer,
        setInnNoNetworkDisclaimer,
        service,
        setServiceTypeDisclaimer,
        serviceTypeDisclaimer,
        setDentalPolicyDisclaimer,
        dentalPolicyDisclaimer,
    } = props;
    const { memberDetails } = useContext(FormContext);
    const history = useHistory();
    /**
     * These disclaimers will be added to the GENERIC_DISCLAIMERs after a revamp of disclaimers in the future.
     */
    const noCoverageDisclaimerText = 'Your plan does not provide coverage for this service.';
    const noNetworkDisclaimerText = 'We are unable to determine your network of providers.';
    const revPercentBilledChargesDisclaimerText =
        "We're unable to display a cost estimate because provider payment is based on a percentage of billed charges. While some data is available to provide an estimate, we recommend that you contact your provider to get an estimate for this service. To search for a different service, here are few ways to search:";
    const revCptNeededDisclaimerText =
        "We're unable to display a cost estimate without knowing more information. The item you searched for may be part of a more comprehensive procedure. To provide you with the best estimate, here are few ways to search:";
    const revUnableToValidateDisclaimerText =
        "We're unable to display a cost estimate due to lack of billing history for this service. Please contact your provider to confirm that your provider supplies this service. To search for a different service, here are few ways to search:";
    const revNoEstimateDisclaimerText = `We cannot provide an estimate because this service will not be paid without other codes. For example, a surgery includes services such as a recovery room, drugs given, and a knee brace. Try searching for the main service you are anticipating, like "MRI knee." This way, you can see the total cost.`;
    const disclaimerBullets = [
        'Search for the covered item or service to be performed (Example: MRI Leg, Knee Replacement)',
        'Obtain the billing code for item or service to be performed (Example: CPT 99213, DRG 29888 or HCPCS K1093)',
    ];

    const lowUsageCodeDisclaimerText =
        "We're unable to display a cost estimate due to lack of billing history for this service.";

    const dentalPolicyMessaging = 'We apologize for the inconvenience.';
    const noBenefitsVisionDisclaimerText =
        "Sorry, but we can't give an estimate right now. For information about vision materials and allowances visit the Benefit or Eligibility page of your plan website or call the number on the back of your member ID card.";
    const callDentalPolicy = (
        <span data-testid={'callDentalPhoneNumber'}>
            {dentalPolicyMessaging} In order to get your dental coverage information, please call
            the member phone number on your <span style={{ fontWeight: 700 }}>DENTAL </span>plan ID
            card.
        </span>
    );

    const { udSourceCode, allsaversOrGriDental, studentResourcesDental } =
        shouldShowDentalPolicyDisclaimer(
            service?.value?.fullCarePath?.coverageTypeCode,
            memberDetails
        );

    /**
     * If member has no dental networks and selects a dental service,
     * we will not display the link to click here for the average cost for this service
     * @todo get rid of this check if we determine a way to get GAC only INN estimates for dental in future
     * @param {*} service selected service object
     */
    const isDentalService = () => {
        return service?.value?.fullCarePath?.coverageTypeCode == CARE_PATH_CATEGORY_CODE.DENTAL;
    };

    const checkLowUsageCodeDisclaimerCondition = () => {
        return (
            service?.value?.fullCarePath?.serviceTypes?.includes(LOW_USAGE_CODE) &&
            service?.value?.fullCarePath?.serviceTypes.includes(REV_DUPLICATE)
        );
    };
    /**
     * Checks If serviceTypes has no benefits and and vision materials carepath selected
     * @param {*} service selected service object
     */
    const checkNoBenefitsVisionDisclaimerCondition = () => {
        return (
            service?.value?.fullCarePath?.serviceTypes?.includes(VISION_MATERIALS) &&
            service?.value?.fullCarePath?.serviceTypes?.includes(NO_BENEFITS)
        );
    };

    /**
     * Returns disclaimer text based on conditions
     * @param {*} service selected service object
     */
    const getServiceTypeDisclaimerText = () => {
        let disclaimerText = '';
        service?.value?.fullCarePath?.serviceTypes.forEach((serviceType) => {
            switch (serviceType) {
                case 'RevPercentBilledCharges':
                    disclaimerText = revPercentBilledChargesDisclaimerText;
                    break;
                case 'RevCptNeeded':
                case 'Unlisted':
                    disclaimerText = revCptNeededDisclaimerText;
                    break;
                case 'RevUnableToValidate':
                    disclaimerText = revUnableToValidateDisclaimerText;
                    break;
                case 'RevNoEstimate':
                    disclaimerText = revNoEstimateDisclaimerText;
                    break;
                case 'LowUsageCode':
                    disclaimerText = lowUsageCodeDisclaimerText;
                    break;
                case 'VisionMaterials':
                    disclaimerText = noCoverageDisclaimerText;
                    break;
                case 'BehavioralMedical':
                    disclaimerText = lowUsageCodeDisclaimerText;
                    break;
                case 'NoBenefits':
                    if (checkForUMREINoBenefits()) {
                        disclaimerText = UMR_ENI_VISION_NOBENEFITS.text;
                    } else {
                        disclaimerText = noBenefitsVisionDisclaimerText;
                    }
                    break;
                case 'RevDuplicate':
                    disclaimerText = lowUsageCodeDisclaimerText;
                    break;
                default:
                    disclaimerText = '';
                    break;
            }
        });
        return disclaimerText;
    };

    const getDisclaimerText = () => {
        let disclaimerText = '';
        if (serviceTypeDisclaimer) {
            disclaimerText = getServiceTypeDisclaimerText();
        } else if (noCoverageDisclaimerOpen) {
            disclaimerText = noCoverageDisclaimerText;
        } else if (dentalPolicyDisclaimer) {
            disclaimerText = determineDentalPolicyDisclaimerText();
        } else {
            disclaimerText = noNetworkDisclaimerText;
        }
        return disclaimerText;
    };

    const getBulletPointsText = () => {
        return (
            <Grid item style={{ paddingLeft: '15px', margin: '0px' }}>
                {disclaimerBullets.length > 1 &&
                    disclaimerBullets.map((text, indexz) => (
                        <List
                            sx={{ listStyleType: 'disc', padding: '0px' }}
                            key={`disclaimerBullet-${indexz}`}
                        >
                            <ListItem sx={{ display: 'list-item', padding: '0px' }}>
                                <Typography.Paragraph>{text}</Typography.Paragraph>
                            </ListItem>
                        </List>
                    ))}
            </Grid>
        );
    };

    const determineDentalPolicyDisclaimerText = () => {
        if (studentResourcesDental) {
            return callDentalPolicy;
        }
        return dentalPolicyMessaging;
    };

    const determineLinkVerbiageForDentalPolicyMessage = () => {
        let coverageType;
        if (udSourceCode) {
            coverageType = 'MEDICAL';
        } else if (allsaversOrGriDental) {
            coverageType = 'DENTAL';
        }

        if (coverageType) {
            return (
                <span data-testid="clickToReenterIdAndGroup">
                    <span>
                        {' '}
                        In order to get your {coverageType.toLowerCase()} coverage information,
                        please{' '}
                    </span>
                    <span>{renderLinkToLoginPage()}</span>
                    <span> and enter the Member ID and Group Number from your </span>
                    <span style={{ fontWeight: 700 }}>{coverageType}</span>
                    <span> plan ID card.</span>
                </span>
            );
        }
        return '';
    };

    const renderLinkToLoginPage = () => {
        return (
            <Typography.Link
                hidden={!dentalPolicyDisclaimer || studentResourcesDental}
                style={{
                    cursor: CURSOR_POINTER,
                    marginBottom: '0px',
                }}
                onClick={() => {
                    history.push(LOGIN_ROUTE);
                }}
                onKeyDown={(e) => {
                    if (e.key == 'Enter') {
                        history.push(LOGIN_ROUTE);
                    }
                }}
                tabIndex="0"
            >
                {'click here'}
            </Typography.Link>
        );
    };

    const checkToShowCloseIcon = () => {
        return (
            serviceTypeDisclaimer &&
            checkNoBenefitsVisionDisclaimerCondition() &&
            (checkForRevcodeServiceType(service?.value?.fullCarePath?.serviceTypes) ||
                checkLowUsageCodeDisclaimerCondition() ||
                checkBehavioralMedicalDisclaimerCondition(service?.value?.fullCarePath))
        );
    };

    const getDisclaimerHeading = () => {
        if (
            (serviceTypeDisclaimer &&
                (checkNoBenefitsVisionDisclaimerCondition() ||
                    checkForRevcodeServiceType(service?.value?.fullCarePath?.serviceTypes))) ||
            checkLowUsageCodeDisclaimerCondition() ||
            checkBehavioralMedicalDisclaimerCondition(service?.value?.fullCarePath) ||
            dentalPolicyDisclaimer
        ) {
            return `Attention:`;
        }
        return `Note: `;
    };

    const checkForUMREINoBenefits = () => {
        return (
            memberDetails?.sourceCode == UMR_POPULATIONS &&
            memberDetails?.lineOfBusiness == LINE_OF_BUSINESS.ENI &&
            serviceTypeDisclaimer &&
            service?.value?.fullCarePath?.serviceTypes?.includes(NO_BENEFITS) &&
            service?.value?.fullCarePath?.serviceTypes?.includes(VISION_MATERIALS)
        );
    };

    /**
     * Returns Background color for disclaimer based on conditions.
     * @param {*} service selected service object
     */
    const getBgColor = () => {
        return serviceTypeDisclaimer &&
            (checkForRevcodeServiceType(service?.value?.fullCarePath?.serviceTypes) ||
                checkForUMREINoBenefits())
            ? '#FEF9EA'
            : '#E5F8FB';
    };

    const getIcon = () => {
        if (
            serviceTypeDisclaimer &&
            (checkForRevcodeServiceType(service?.value?.fullCarePath?.serviceTypes) ||
                checkForUMREINoBenefits())
        ) {
            return (
                <Grid
                    item
                    sx={{
                        position: 'relative',
                    }}
                >
                    <Square
                        style={{
                            color: '#19191A',
                        }}
                        sx={{
                            position: 'absolute',
                            height: '12px',
                            width: '15px',
                            left: '5px',
                            top: '8.5px',
                        }}
                    />
                    <Warning
                        sx={{
                            position: 'sticky',
                            zIndex: '1',
                        }}
                        style={{
                            color: '#F5B700',
                        }}
                    />
                </Grid>
            );
        } else if (
            serviceTypeDisclaimer &&
            (checkNoBenefitsVisionDisclaimerCondition() ||
                checkLowUsageCodeDisclaimerCondition() ||
                checkBehavioralMedicalDisclaimerCondition(service?.value?.fullCarePath))
        ) {
            return (
                <Error
                    style={{
                        color: '#002677',
                    }}
                />
            );
        } else {
            return <FlagIcon style={{ color: '#002677' }} />;
        }
    };

    return (
        <Box
            component="div"
            px={3}
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: getBgColor(),
                minHeight: '72px',
                marginBottom: '12px',
            }}
            data-testid="serviceDisclaimer"
        >
            <Box>
                <div key={`service-disclaimer-div`}>
                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justifyContent="flex-start"
                        py={1}
                        sx={checkToShowCloseIcon() ? { padding: '25px 0' } : { padding: '0 0' }}
                        role="alert"
                        wrap="nowrap"
                    >
                        <Grid item>{getIcon()}</Grid>
                        <Grid item>
                            <Grid
                                container
                                direction="column"
                                sx={{ paddingLeft: '10px' }}
                                data-testid="serviceDisclaimerTest"
                            >
                                {' '}
                                <Grid item>
                                    <Typography.Paragraph
                                        data-testid={`disclaimerMessage-${service?.value?.fullCarePath?.serviceTypes}`}
                                        style={{
                                            lineHeight: '22px',
                                            color: '#333333',
                                        }}
                                    >
                                        <span style={{ fontWeight: 700, marginRight: '5px' }}>
                                            {getDisclaimerHeading()}
                                        </span>
                                        <span data-testid="disclaimer-text">
                                            {getDisclaimerText()}
                                        </span>
                                        <span>{determineLinkVerbiageForDentalPolicyMessage()}</span>
                                    </Typography.Paragraph>
                                </Grid>
                                {serviceTypeDisclaimer &&
                                checkForRevcodeServiceType(
                                    service?.value?.fullCarePath?.serviceTypes
                                ) &&
                                !checkForRevNoEstimateServiceType(
                                    service?.value?.fullCarePath?.serviceTypes
                                )
                                    ? getBulletPointsText()
                                    : ''}
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Typography.Link
                                //TODO Focus on link on render
                                id="linkToAverageCost"
                                hidden={
                                    serviceTypeDisclaimer ||
                                    noCoverageDisclaimerOpen ||
                                    dentalPolicyDisclaimer ||
                                    isDentalService()
                                }
                                style={{ cursor: CURSOR_POINTER, marginBottom: '0px' }}
                                onClick={() => {
                                    setProvider(SHELL_INN_PROVIDER);
                                    handleSubmit({
                                        gacOnly: true,
                                    });
                                }}
                                onKeyDown={(e) => {
                                    if (e.key == 'Enter') {
                                        setProvider(SHELL_INN_PROVIDER);
                                        handleSubmit({
                                            gacOnly: true,
                                        });
                                    }
                                }}
                                tabIndex={0}
                                aria-labelledby="linkToAverageCost"
                            >
                                {`Click here for the average cost for this service in ${
                                    isIndemnity[service?.value?.fullCarePath?.coverageTypeCode]
                                        ? 'your'
                                        : ''
                                } area.`}
                            </Typography.Link>
                        </Grid>
                    </Grid>
                </div>
            </Box>
            {!checkToShowCloseIcon() ? (
                <IconButton
                    style={{ cursor: CURSOR_POINTER }}
                    onClick={() => {
                        setInnNoNetworkDisclaimer(false);
                        setNoCoverageDisclaimer(false);
                        setServiceTypeDisclaimer(false);
                        setDentalPolicyDisclaimer(false);
                    }}
                    onKeyDown={(e) => {
                        if (e.key == ENTER_KEY || e.key == ' ') {
                            setInnNoNetworkDisclaimer(false);
                            setNoCoverageDisclaimer(false);
                            setServiceTypeDisclaimer(false);
                            setDentalPolicyDisclaimer(false);
                        }
                    }}
                    tabIndex={0}
                    aria-label="closeServiceDisclaimer"
                    sx={{
                        '&:focus': {
                            outline: '#196ecf auto 1px',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : (
                ''
            )}
        </Box>
    );
};
export default ServiceDisclaimerBanner;
